import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Scale, MapPin, Languages, Award, ArrowRight, Phone, MessageCircle, Star,
  ChevronDown, ChevronUp, Building2, IdCard, HelpCircle, BookOpen,
  Heart, ShieldCheck, RefreshCw, RotateCcw, Shield, Lock, Banknote,
  FileX, Gavel, Briefcase, Clock, UserX, Globe, CreditCard, Home,
  Users, TreePine, FlaskConical, Baby, ShieldAlert, Fingerprint, Vote,
  UserCheck, ShoppingCart, Leaf, Receipt, TrendingUp, Radio, LineChart,
  Landmark, Lightbulb, Plane, FileSignature, PlusCircle, X, Filter, CalendarCheck
} from "lucide-react";
import api from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import Seo from "@/components/Seo";
import { PAGE_SEO, attorneyJsonLd, breadcrumbJsonLd, scLawyersFaqJsonLd, SC_LAWYERS_FAQS, legalServiceJsonLd } from "@/lib/seo";
import { SC_EDITORIAL } from "@/lib/lawyerContent";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import imageUrl from "@/lib/imageUrl";
import { CITIES } from "@/pages/CityLawyer";
import { OnlineBadge } from "@/components/OnlineBadge";
import { PRACTICE_AREA_LIST } from "@/lib/practiceAreaContent";
import { CityHubLinks } from "@/components/CityHubLinks";

const PA_ICON_MAP = {
  Scale, Heart, ShieldCheck, RefreshCw, RotateCcw, Shield, Lock,
  Banknote, FileX, Gavel, Briefcase, Clock, UserX, Building2,
  Home, Globe, CreditCard, MapPin, Users, TreePine,
  FlaskConical, Baby, ShieldAlert, Fingerprint, Vote, UserCheck,
  ShoppingCart, Leaf, Receipt, TrendingUp, Radio, LineChart,
  Landmark, Lightbulb, Plane, FileSignature,
};
function PAIcon({ name, className }) {
  const Icon = PA_ICON_MAP[name] || Scale;
  return <Icon className={className} />;
}

const waLink = (num) => `https://wa.me/${(num || "").replace(/[^0-9]/g, "")}`;
const BIO_LIMIT = 130;

function LawyerCard({ lw, i, lang, L, ctaLabel }) {
  const [expanded, setExpanded] = useState(false);
  const bio = L(lw, "bio") || "";
  const isLong = bio.length > BIO_LIMIT;
  const displayBio = expanded || !isLong ? bio : bio.slice(0, BIO_LIMIT) + "\u2026";

  return (
    <motion.div
      key={lw.id}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(i * 0.05, 0.3) }}
      data-testid="sc-lawyer-card"
      className="card-hover flex h-full flex-col rounded-2xl border bg-card p-3 sm:p-6"
    >
      <div className="flex flex-col items-center gap-2 text-center sm:flex-row sm:items-start sm:gap-4 sm:text-left">
        <Avatar className="h-12 w-12 sm:h-16 sm:w-16">
          <AvatarImage src={imageUrl(lw.photo, { w: 160 })} alt={lw.name} width={64} height={64} loading="lazy" decoding="async" className="object-cover" />
          <AvatarFallback>{lw.name?.[0]}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center justify-center gap-1.5 sm:justify-start sm:gap-2">
            <h2 className="font-serif text-sm font-bold leading-tight sm:text-lg">{lw.name}</h2>
            {lw.is_founder && (
              <Badge className="gap-1 bg-amber-400 text-[10px] text-amber-950 hover:bg-amber-400 sm:text-xs" data-testid="founder-badge">
                <Star className="h-3 w-3" /> {lang === "hi" ? "\u0938\u0902\u0938\u094d\u0925\u093e\u092a\u0915" : "Founder"}
              </Badge>
            )}
          </div>
          <p className="mt-0.5 text-xs font-medium text-primary sm:text-sm">{L(lw, "specialization")}</p>
          {lw.online_available && <div className="mt-1.5 flex justify-center sm:justify-start"><OnlineBadge lang={lang} /></div>}
        </div>
      </div>

      {bio && (
        <div className="mt-3">
          <p className="line-clamp-4 text-xs text-muted-foreground leading-relaxed sm:line-clamp-none sm:text-sm">{displayBio}</p>
          {isLong && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-1 hidden items-center gap-0.5 text-xs font-semibold text-primary hover:underline sm:inline-flex"
            >
              {expanded ? <><ChevronUp className="h-3 w-3" /> See less</> : <><ChevronDown className="h-3 w-3" /> See more</>}
            </button>
          )}
        </div>
      )}

      <div className="mt-3 space-y-1 text-xs text-muted-foreground sm:space-y-1.5 sm:text-sm">
        {lw.experience_years ? <p className="flex items-center gap-1.5 sm:gap-2"><Award className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" /> {lw.experience_years}+ {lang === "hi" ? "\u0935\u0930\u094d\u0937 \u0905\u0928\u0941\u092d\u0935" : "yrs exp."}</p> : null}
        {lw.location ? <p className="flex items-center gap-1.5 sm:gap-2"><MapPin className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" /> <span className="truncate">{lw.location}</span></p> : null}
        {lw.languages?.length ? <p className="flex items-center gap-1.5 sm:gap-2"><Languages className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" /> <span className="truncate">{lw.languages.join(", ")}</span></p> : null}
        {lw.bar_council_id ? <p className="hidden items-center gap-2 sm:flex"><IdCard className="h-4 w-4 shrink-0" /> {lang === "hi" ? "बार काउंसिल नामांकन" : "Bar Council enrolment"}: <span className="font-mono text-xs">{lw.bar_council_id}</span></p> : null}
      </div>

      {L(lw, "helps")?.length ? (
        <div className="mt-3 hidden flex-wrap gap-1.5 sm:flex">
          {(lang === "hi" ? lw.helps_hi || lw.helps_en : lw.helps_en).slice(0, 4).map((h, hi) => (
            <span key={hi} className="rounded-full bg-secondary px-2.5 py-0.5 text-xs text-secondary-foreground">{h}</span>
          ))}
        </div>
      ) : null}

      <div className="mt-auto flex items-center gap-1.5 pt-4 sm:gap-2 sm:pt-5">
        <Button asChild className="h-9 flex-1 px-2 text-xs sm:h-10 sm:px-4 sm:text-sm" data-testid={`view-profile-${lw.id}`}>
          <Link to={`/supreme-court-lawyers/${lw.id}`} className="flex items-center justify-center gap-1 truncate">
            <span className="truncate">{ctaLabel || (lang === "hi" ? "\u092a\u094d\u0930\u094b\u092b\u093c\u093e\u0907\u0932 \u0926\u0947\u0916\u0947\u0902" : "View profile")}</span> <ArrowRight className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
          </Link>
        </Button>
        {lw.phone && (
          <Button asChild variant="outline" size="icon" aria-label="Call" className="hidden shrink-0 sm:inline-flex">
            <a href={`tel:${lw.phone.replace(/\s/g, "")}`}><Phone className="h-4 w-4" /></a>
          </Button>
        )}
        {(lw.whatsapp || lw.phone) && (
          <Button asChild variant="outline" size="icon" aria-label="WhatsApp" className="hidden shrink-0 sm:inline-flex">
            <a href={waLink(lw.whatsapp || lw.phone)} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4" /></a>
          </Button>
        )}
      </div>
    </motion.div>
  );
}

export default function SupremeCourtLawyers() {
  const { L, lang } = useLanguage();
  const { data: lawyers, isLoading } = useQuery({
    queryKey: ["sc-lawyers"],
    queryFn: async () => (await api.get("/public/lawyers")).data,
  });

  // Interactive city + practice-area filters — dynamically narrow the advocate
  // grid on this same page instead of forcing a navigation away.
  const [activeCity, setActiveCity] = useState("");
  const [activePractice, setActivePractice] = useState("");

  const practiceOptions = useMemo(
    () => Array.from(new Set((lawyers || []).map((l) => l.specialization_en).filter(Boolean))),
    [lawyers]
  );

  const filteredLawyers = useMemo(() => {
    return (lawyers || []).filter((lw) => {
      if (activeCity && !(lw.location || "").toLowerCase().includes(activeCity.toLowerCase())) return false;
      if (activePractice && lw.specialization_en !== activePractice) return false;
      return true;
    });
  }, [lawyers, activeCity, activePractice]);

  const filterActive = !!(activeCity || activePractice);
  const activeCityObj = CITIES.find((c) => c.name === activeCity);

  const jsonLd = [
    breadcrumbJsonLd([
      { name: "Home", path: "/" },
      { name: "Supreme Court Lawyers", path: "/supreme-court-lawyers" },
    ]),
    legalServiceJsonLd({ lawyers: lawyers || [] }),
    scLawyersFaqJsonLd(),
    ...(lawyers || []).map(attorneyJsonLd).filter(Boolean),
  ];

  return (
    <div>
      <Seo {...PAGE_SEO.lawyers} jsonLd={jsonLd} />

      <section className="li-hero-gradient border-b">
        <div className="mx-auto w-full max-w-6xl px-4 py-14 sm:px-6">
          <h1 className="font-serif text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
            {lang === "hi" ? "\u0939\u092e\u093e\u0930\u0947 \u0936\u0940\u0930\u094d\u0937 \u0938\u0941\u092a\u094d\u0930\u0940\u092e \u0915\u094b\u0930\u094d\u091f \u0935\u0915\u0940\u0932 \u090f\u0935\u0902 \u0905\u0927\u093f\u0935\u0915\u094d\u0924\u093e" : "Our Top Supreme Court Lawyers & Advocates"}
          </h1>
          <p className="mt-3 max-w-3xl text-sm text-muted-foreground sm:text-base">
            {lang === "hi"
              ? "\u092d\u093e\u0930\u0924 \u0915\u0947 \u0938\u0941\u092a\u094d\u0930\u0940\u092e \u0915\u094b\u0930\u094d\u091f \u0915\u0947 \u0905\u0928\u0941\u092d\u0935\u0940 \u0935\u0915\u0940\u0932 \u0914\u0930 \u0935\u0930\u093f\u0937\u094d\u0920 \u0905\u0927\u093f\u0935\u0915\u094d\u0924\u093e, \u091c\u094b \u091c\u092e\u093e\u0928\u0924, \u0906\u092a\u0930\u093e\u0927\u093f\u0915, \u0938\u0902\u0935\u0948\u0927\u093e\u0928\u093f\u0915 \u0914\u0930 \u0905\u0928\u094d\u092f \u092e\u093e\u092e\u0932\u094b\u0902 \u092e\u0947\u0902 \u0928\u093f\u0903\u0936\u0941\u0932\u094d\u0915 \u092e\u093e\u0930\u094d\u0917\u0926\u0930\u094d\u0936\u0928 \u0926\u0947\u0924\u0947 \u0939\u0948\u0902\u0964"
              : "Experienced Supreme Court of India lawyers, senior advocates and legal experts offering free, pro bono guidance on bail, criminal, constitutional, service and family matters. View a profile and book a free consultation."}
          </p>
        </div>
      </section>

      {/* Browse by City — interactive filter, narrows the grid below on this same page */}
      <section className="border-b bg-secondary/20" data-testid="city-grid-section">
        <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6">
          <div className="flex flex-wrap items-center gap-2">
            <div className="mb-1 flex w-full items-center gap-1.5 text-sm font-semibold text-foreground sm:mb-0 sm:mr-2 sm:w-auto">
              <Building2 className="h-4 w-4 shrink-0 text-primary" />
              {lang === "hi" ? "शहर के अनुसार फ़िल्टर करें:" : "Filter by city:"}
            </div>
            {CITIES.map((c) => (
              <button
                key={c.slug}
                type="button"
                onClick={() => setActiveCity(activeCity === c.name ? "" : c.name)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border min-h-[40px] px-3.5 py-2 text-sm font-medium transition-colors",
                  activeCity === c.name
                    ? "border-primary bg-primary text-primary-foreground shadow-sm"
                    : "border-primary/20 hover:border-primary/40 hover:bg-primary/10 hover:text-primary"
                )}
                data-testid={`city-btn-${c.slug}`}
                aria-pressed={activeCity === c.name}
              >
                <MapPin className="h-3.5 w-3.5" /> {c.name}
              </button>
            ))}
          </div>
          {activeCityObj && (
            <Link
              to={`/supreme-court-lawyer/${activeCityObj.slug}`}
              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
              data-testid="view-full-city-guide"
            >
              {lang === "hi" ? `${activeCity} के लिए पूरी गाइड देखें` : `View full ${activeCity} city guide`} <ArrowRight className="h-3 w-3" />
            </Link>
          )}
        </div>
      </section>

      {/* Filter by practice area — interactive chips derived from the advocates below */}
      {practiceOptions.length > 1 && (
        <section className="border-b bg-secondary/10" data-testid="practice-filter-section">
          <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6">
            <div className="flex flex-wrap items-center gap-2">
              <div className="mb-1 flex w-full items-center gap-1.5 text-sm font-semibold text-foreground sm:mb-0 sm:mr-2 sm:w-auto">
                <Filter className="h-4 w-4 shrink-0 text-primary" />
                {lang === "hi" ? "प्रैक्टिस क्षेत्र के अनुसार फ़िल्टर करें:" : "Filter by practice area:"}
              </div>
              {practiceOptions.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setActivePractice(activePractice === p ? "" : p)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border min-h-[40px] px-3.5 py-2 text-sm font-medium transition-colors",
                    activePractice === p
                      ? "border-primary bg-primary text-primary-foreground shadow-sm"
                      : "border-primary/20 hover:border-primary/40 hover:bg-primary/10 hover:text-primary"
                  )}
                  data-testid={`practice-filter-${p}`}
                  aria-pressed={activePractice === p}
                >
                  <Scale className="h-3.5 w-3.5" /> {p}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="mx-auto w-full max-w-6xl px-4 py-14 sm:px-6">
        {filterActive && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card px-4 py-3" data-testid="active-filters-bar">
            <p className="text-sm text-muted-foreground">
              {lang === "hi" ? `${filteredLawyers.length} अधिवक्ता मिले` : `${filteredLawyers.length} advocate${filteredLawyers.length === 1 ? "" : "s"} found`}
              {activeCity && <> {lang === "hi" ? "में" : "in"} <span className="font-semibold text-foreground">{activeCity}</span></>}
              {activePractice && <> &middot; <span className="font-semibold text-foreground">{activePractice}</span></>}
            </p>
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => { setActiveCity(""); setActivePractice(""); }} data-testid="clear-lawyer-filters">
              <X className="h-3.5 w-3.5" /> {lang === "hi" ? "फ़िल्टर हटाएं" : "Clear filters"}
            </Button>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-3">
          {isLoading
            ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-56 w-full rounded-2xl sm:h-80" />)
            : filteredLawyers.length > 0
            ? filteredLawyers.map((lw, i) => (
                <LawyerCard
                  key={lw.id}
                  lw={lw}
                  i={i}
                  lang={lang}
                  L={L}
                  ctaLabel={filterActive ? (
                    <span className="inline-flex items-center gap-1 truncate"><CalendarCheck className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{lang === "hi" ? "मुफ़्त स्लॉट" : "Free Slot"}</span></span>
                  ) : null}
                />
              ))
            : (
              <p className="col-span-full py-8 text-center text-muted-foreground" data-testid="no-lawyers-found">
                {lang === "hi" ? "इस फ़िल्टर के लिए कोई अधिवक्ता नहीं मिला।" : "No advocates match this filter yet — try a different city or practice area."}
              </p>
            )}
        </div>
      </section>

      {/* Editorial: long-form guidance */}
      <section className="border-t bg-secondary/30" data-testid="sc-lawyers-editorial">
        <div className="mx-auto w-full max-w-4xl px-4 py-14 sm:px-6">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary"><BookOpen className="h-4 w-4" /> Editorial guide</div>
          <h2 className="mt-2 font-serif text-2xl font-bold tracking-tight sm:text-3xl">
            Supreme Court advocates, senior counsel and how to choose the right one
          </h2>
          <p className="mt-3 reading-prose text-muted-foreground">
            The advocates listed above practise before the Supreme Court of India across criminal, constitutional, service, property, commercial and family matters, and each profile sets out their specialisations, practice areas, city, Bar Council enrolment and years of experience. The guide below explains what makes Supreme Court representation distinct, how to choose an advocate, what a case actually involves, and when senior counsel is worth briefing.
          </p>
          <div className="mt-8 space-y-10">
            {SC_EDITORIAL.map((sec) => (
              <article key={sec.id} id={sec.id} data-testid={`editorial-${sec.id}`}>
                <h3 className="font-serif text-xl font-bold tracking-tight">{sec.heading}</h3>
                <div className="mt-3 space-y-4 reading-prose text-muted-foreground">
                  {sec.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </article>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild className="grad-cta text-white">
              <Link to="/free-legal-help">Request Free Guidance</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Practice Areas Grid */}
      <section className="border-t bg-secondary/30" data-testid="practice-areas-section">
        <div className="mx-auto w-full max-w-6xl px-4 py-14 sm:px-6">
          <div className="mb-8">
            <Badge className="mb-3 gap-1.5 bg-primary/10 text-primary hover:bg-primary/10">
              <Scale className="h-3.5 w-3.5" /> Practice Areas
            </Badge>
            <h2 className="font-serif text-2xl font-bold tracking-tight sm:text-3xl">
              Supreme Court Lawyers by Practice Area
            </h2>
            <p className="mt-2 max-w-3xl text-muted-foreground text-[15px]">
              Find a dedicated Supreme Court advocate for your specific legal matter. Each link below has a full guide with legal provisions, process steps, limitation periods, FAQs, and free consultation access.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="practice-areas-grid">
            {PRACTICE_AREA_LIST.slice(0, 20).map((area) => (
              <Link
                key={area.slug}
                to={`/supreme-court-lawyer-for/${area.slug}`}
                className="card-hover group flex items-start gap-3 rounded-xl border bg-card p-4 transition-shadow"
                data-testid={`practice-area-card-${area.slug}`}
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 transition-colors group-hover:bg-primary/20">
                  <PAIcon name={area.icon} className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm leading-snug group-hover:text-primary transition-colors">
                    {area.title}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground truncate">{area.badge}</p>
                </div>
                <ArrowRight className="ml-1 h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 mt-0.5" />
              </Link>
            ))}
          </div>

          {/* More Practice Areas (additional 20 pages) */}
          <div className="mt-12 mb-8">
            <Badge className="mb-3 gap-1.5 bg-primary/10 text-primary hover:bg-primary/10" data-testid="more-practice-areas-badge">
              <PlusCircle className="h-3.5 w-3.5" /> More Practice Areas
            </Badge>
            <h3 className="font-serif text-xl font-bold tracking-tight sm:text-2xl">
              More Supreme Court Lawyer & Advocate Services
            </h3>
            <p className="mt-2 max-w-3xl text-muted-foreground text-[15px]">
              Additional specialised matters handled by our Supreme Court lawyers and advocates, from tribunal appeals to Advocate-on-Record filing.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="more-practice-areas-grid">
            {PRACTICE_AREA_LIST.slice(20).map((area) => (
              <Link
                key={area.slug}
                to={`/supreme-court-lawyer-for/${area.slug}`}
                className="card-hover group flex items-start gap-3 rounded-xl border bg-card p-4 transition-shadow"
                data-testid={`practice-area-card-${area.slug}`}
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 transition-colors group-hover:bg-primary/20">
                  <PAIcon name={area.icon} className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm leading-snug group-hover:text-primary transition-colors">
                    {area.title}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground truncate">{area.badge}</p>
                </div>
                <ArrowRight className="ml-1 h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 mt-0.5" />
              </Link>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild className="grad-cta text-white" data-testid="sc-practice-areas-cta">
              <Link to="/free-legal-help">
                <Phone className="mr-1.5 h-4 w-4" />
                Not sure which applies? Talk to a Supreme Court Advocate — free
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t" data-testid="sc-lawyers-faq">
        <div className="mx-auto w-full max-w-4xl px-4 py-14 sm:px-6">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary"><HelpCircle className="h-4 w-4" /> Frequently asked questions</div>
          <h2 className="mt-2 font-serif text-2xl font-bold tracking-tight sm:text-3xl">Finding and working with a Supreme Court lawyer</h2>
          <Accordion type="single" collapsible className="mt-6">
            {SC_LAWYERS_FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} data-testid={`sc-faq-item-${i}`}>
                <AccordionTrigger className="text-left font-semibold">{f.q}</AccordionTrigger>
                <AccordionContent className="reading-prose text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <CityHubLinks />
    </div>
  );
}
